const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:4000').replace(/\/$/, '');

export interface ApiWarehouse {
  id: string;
  code: string;
  name: string;
}

export interface ApiStorageUnit {
  id: string;
  externalCode: string;
  type: 'bag' | 'box' | 'pallet' | 'item';
  status: 'new' | 'opened' | 'sorting' | 'ready' | 'reserved' | 'shipped' | 'defect';
  warehouseId: string;
  address?: string | null;
  addressLatin?: string | null;
  locationCode?: string | null;
  sourceCode?: string | null;
  comment?: string | null;
  createdAt: string;
}

export interface ApiMovement {
  id: string;
  unitId: string;
  type: 'receive' | 'place' | 'move' | 'reserve' | 'ship' | 'writeoff';
  fromLocation?: string | null;
  toLocation?: string | null;
  quantity: number;
  actorUserId?: string | null;
  comment?: string | null;
  createdAt: string;
  unit?: ApiStorageUnit;
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
    ...init,
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `HTTP ${resp.status}`);
  }
  return (await resp.json()) as T;
}

export const warehouseApi = {
  listWarehouses() {
    return apiFetch<ApiWarehouse[]>('/api/warehouses');
  },
  createWarehouse(payload: { code: string; name: string }) {
    return apiFetch<ApiWarehouse>('/api/warehouses', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  listStorageUnits(warehouseId?: string) {
    const q = warehouseId ? `?warehouseId=${encodeURIComponent(warehouseId)}` : '';
    return apiFetch<ApiStorageUnit[]>(`/api/storage-units${q}`);
  },
  createStorageUnit(payload: {
    externalCode: string;
    type: ApiStorageUnit['type'];
    status: ApiStorageUnit['status'];
    warehouseId: string;
    address?: string;
    addressLatin?: string;
    locationCode?: string;
    sourceCode?: string;
    comment?: string;
  }) {
    return apiFetch<ApiStorageUnit>('/api/storage-units', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  updateStorageUnit(
    id: string,
    payload: Partial<{
      externalCode: string;
      type: ApiStorageUnit['type'];
      status: ApiStorageUnit['status'];
      warehouseId: string;
      address: string;
      addressLatin: string;
      locationCode: string;
      sourceCode: string;
      comment: string;
    }>,
  ) {
    return apiFetch<ApiStorageUnit>(`/api/storage-units/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
  createMovement(payload: {
    unitId: string;
    type: ApiMovement['type'];
    fromLocation?: string;
    toLocation?: string;
    quantity?: number;
    comment?: string;
    actorUserId?: string;
  }) {
    return apiFetch<ApiMovement>('/api/movements', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  listMovements() {
    return apiFetch<ApiMovement[]>('/api/movements');
  },
};

